import { Dispatch, FC, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthProvider";
import { useClickOutOfElement } from "../../custom-hooks/base-hooks/useClickOutOfElement";
import { useLocation } from "react-router-dom";
import MenuIconSVG from "../shared/svg/console/MenuIconSVG";
import SidebarLogoutSVG from "../shared/svg/console/SidebarLogoutSVG";
import UsersPageIconSVG from "../shared/svg/console/UsersPageIconSVG";
import RolesPageIconSVG from "../shared/svg/console/RolesPageIconSVG";
import ProductFeaturesPageIconSVG from "../shared/svg/console/ProductFeaturesPageIconSVG";
import NewsIconSVG from "../shared/svg/NewsSVG";
import SystemEventsIconSVG from "../shared/svg/console/SystemEventsIconSVG";
import SidebarItem from "./SideBarItem";
import ConsoleLangSelect from "./ConsoleLangSelect";
import ConsoleThemeToggle from "./ConsoleThemeToggle";
import clsx from "clsx";
import { useTheme } from "../../domain/services/themeService";
import CustomLangIcon from "../../custom-components/CustomLangIcon";
import ManagementIconSVG from "../shared/svg/ManagementIconSVG";
import EventsIconSVG from "../shared/svg/EventsIconSVG";
import PartnersIconSVG from "../shared/svg/PartnersIconSVG";
import DevelopmentHistoryIconSVG from "../shared/svg/DevelopmentHistoryIconSVG";
import ContactsIconSVG from "../shared/svg/ContactsIconSVG";
import CoursesIconSVG from "../shared/svg/CoursesIconSVG";

interface ConsoleSidebarProps {
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
}

const ConsoleSidebar: FC<ConsoleSidebarProps> = ({ isExpanded, setIsExpanded }) => {
    const { t } = useTranslation();
    const { signOut } = useAuth();
    const sidebarRef = useRef(null);
    const location = useLocation()?.pathname;
    const [langPopupOpen, setLangPopupOpen] = useState(false);

    useClickOutOfElement(sidebarRef, () => setIsExpanded(false));

    const { theme, toggleTheme } = useTheme();

    const routes = [
        {
            to: "/console/users",
            icon: <UsersPageIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.usersPage"),
            active: location.includes("/users"),
        },
        {
            to: "/console/roles",
            icon: <RolesPageIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.rolesPage"),
            active: location.includes("/roles"),
        },
        {
            to: "/console/productFeatures",
            icon: (
                <ProductFeaturesPageIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />
            ),
            label: t("console.sidebar.productFeaturesPage"),
            active: location.includes("/productFeatures"),
        },
        {
            to: "/console/news",
            icon: <NewsIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.newsPage"),
            active: location.includes("/news"),
        },
        {
            to: "/console/events",
            icon: <EventsIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.eventsPage"),
            active: location.includes("/events"),
        },
        {
            to: "/console/systemEvents",
            icon: <SystemEventsIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.systemEventsPage"),
            active: location.includes("/systemEvents"),
        },
        {
            to: "/console/management",
            icon: <ManagementIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:fill-dark-fill-100" />,
            label: t("console.sidebar.managementPage"),
            active: location.includes("/management"),
        },
        {
            to: "/console/partners",
            icon: <PartnersIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:fill-dark-fill-100" />,
            label: t("console.sidebar.partnersPage"),
            active: location.includes("/partners"),
        },
        {
            to: "/console/development-history",
            icon: (
                <DevelopmentHistoryIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />
            ),
            label: t("console.sidebar.developmentHistoryPage"),
            active: location.includes("/development-history"),
        },
        {
            to: "/console/contacts",
            icon: <ContactsIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.contactsPage"),
            active: location.includes("/contacts"),
        },
        {
            to: "/console/courses",
            icon: <CoursesIconSVG className="w-7 h-7 flex-shrink-0 text-light-text-100 dark:text-dark-text-100" />,
            label: t("console.sidebar.coursesPage"),
            active: location.includes("/courses"),
        },
    ];

    return (
        <div
            ref={sidebarRef}
            className={clsx(
                "absolute z-[100] flex flex-col justify-between h-full transition-all",
                "bg-light-bg-100 dark:bg-dark-bg-100",
                isExpanded ? "w-[260px]" : "w-[60px]",
            )}
        >
            <div className="relative w-full">
                <SidebarItem
                    icon={<MenuIconSVG className="w-7 h-7 flex-shrink-0 text-light-fill-100 dark:text-dark-fill-100" />}
                    label={t("console.sidebar.menu")}
                    isExpanded={isExpanded}
                    onClick={() => setIsExpanded(!isExpanded)}
                />

                {routes.map((r) => (
                    <SidebarItem key={r.to} {...r} isExpanded={isExpanded} />
                ))}
            </div>

            <div className="relative w-full flex flex-col items-center">
                <SidebarItem
                    isExpanded={isExpanded}
                    label={t("console.sidebar.themetoggle")}
                    onClick={toggleTheme}
                    icon={<ConsoleThemeToggle theme={theme} />}
                />
                <SidebarItem
                    isExpanded={isExpanded}
                    label={t("console.sidebar.languageToggle")}
                    icon={<CustomLangIcon className="w-7 h-7" />}
                    onClick={() => setLangPopupOpen(!langPopupOpen)}
                />
                {langPopupOpen && (
                    <ConsoleLangSelect isExpanded={isExpanded} isOpen={langPopupOpen} setIsOpen={setLangPopupOpen} />
                )}

                <div className={clsx("w-full", isExpanded ? "pl-[0px]" : "flex justify-center")}>
                    <SidebarItem
                        icon={
                            <SidebarLogoutSVG className="w-7 h-7 flex-shrink-0 text-light-fill-100 dark:text-dark-fill-100" />
                        }
                        label={t("console.sidebar.logOut")}
                        isExpanded={isExpanded}
                        onClick={signOut}
                    />
                </div>
            </div>
        </div>
    );
};

export default ConsoleSidebar;
